// frontend/src/pages/admin/EditHomepageContentPage.tsx
import { useState, useEffect, type ChangeEvent, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Settings, Save, Loader2, AlertCircle, Trash2, UploadCloud, PlusCircle, LayoutDashboard } from 'lucide-react'; // GripVertical removed
import { getAllSettings, updateSettings, uploadGeneralImage, getAvailableGeneralImages } from '@/api/settingService';
import { 
  type SiteSetting, 
  type SettingUpdatePayload, 
  type HomepageHeroImage, 
  type HomepageValuePropCard,
  type ImageUploadResponse,
  type AvailableImage,
  type SettingValueType
} from '@/types'; 
import { getFullImageUrl as constructFullImageUrl } from '@/lib/utils'; 
import { toast } from 'sonner';
// Removed react-dnd imports: DndProvider, useDrag, useDrop, DropTargetMonitor, HTML5Backend

const KNOWN_SETTING_KEYS = {
  HOME_HERO_MAIN_TITLE: "home_hero_main_title",
  HOME_HERO_SUBTITLE: "home_hero_subtitle",
  HOME_HERO_IMAGES_JSON: "home_hero_images_json",
  HOME_VALUE_PROP_CARDS_JSON: "home_value_prop_cards_json",
  HOME_CTA_TITLE: "home_cta_title",
  HOME_CTA_SUBTITLE: "home_cta_subtitle",
};

const defaultValuePropCards: HomepageValuePropCard[] = [
    { id: "vp1", icon: "BarChart3", title: "Expert Analysis", description: "Access deep market analysis..." },
    { id: "vp2", icon: "BookOpen", title: "Comprehensive Research", description: "Dive into detailed research..." },
    { id: "vp3", icon: "Lightbulb", title: "Innovative Solutions", description: "Leverage our advanced products..." }
];

// --- HeroImageItem Component (Simplified - No Drag and Drop) ---
interface HeroImageItemProps {
    image: HomepageHeroImage;
    index: number;
    getFullImageUrl: (path: string | null | undefined) => string | undefined;
    handleHeroImageAltTextChange: (index: number, altText: string) => void;
    removeHeroImage: (index: number) => void;
    // Props for editing alt text directly if needed, or manage edit state in parent
    isEditing?: boolean; 
    editingAltText?: string;
    onEditAltText?: (index: number) => void; // Optional: If you want an edit button
    onSaveAltText?: (index: number) => void; // Optional
    setEditingAltText?: (text: string) => void; // Optional
}

const HeroImageItem: React.FC<HeroImageItemProps> = ({
    image, index, getFullImageUrl, handleHeroImageAltTextChange, removeHeroImage
}) => {
    // Basic item display without drag/drop hooks
    return (
        <div className="flex items-center gap-2 mb-2 p-3 border rounded-md bg-muted/20 ">
            {/* Optional: Add an edit icon/button if you want a dedicated edit mode for alt text */}
            <img 
                src={getFullImageUrl(image.url) || "https://placehold.co/100x60/cccccc/333333?text=No+Preview"} 
                alt={image.alt_text || 'Hero image preview'} 
                className="h-16 w-28 object-cover rounded border"
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = "https://placehold.co/100x60/cccccc/333333?text=Error";}}
            />
            <div className="flex-grow space-y-1">
                <Input
                    type="text"
                    placeholder="Relative Image Path"
                    value={image.url}
                    readOnly 
                    className="bg-input/50 text-xs"
                    title="Image path (read-only after upload)"
                />
                <Input
                    type="text"
                    placeholder="Alt Text (recommended)"
                    value={image.alt_text || ''}
                    onChange={(e) => handleHeroImageAltTextChange(index, e.target.value)}
                    className="text-xs"
                />
            </div>
            <Button variant="ghost" size="icon" onClick={() => removeHeroImage(index)} aria-label="Remove image" className="text-destructive hover:bg-destructive/10 flex-shrink-0">
                <Trash2 className="h-4 w-4" />
            </Button>
        </div>
    );
};


const EditHomepageContentPage = () => {
  const [initialSettingsMap, setInitialSettingsMap] = useState<Record<string, SiteSetting>>({});
  const [currentSettingsMap, setCurrentSettingsMap] = useState<Record<string, SiteSetting>>({});
  
  const [heroImages, setHeroImages] = useState<HomepageHeroImage[]>([]);
  const [initialHeroImagesJson, setInitialHeroImagesJson] = useState<string>("[]");
  const [newHeroImageFile, setNewHeroImageFile] = useState<File | null>(null);
  const [newHeroImageAltText, setNewHeroImageAltText] = useState<string>("");
  const [isUploadingHeroImage, setIsUploadingHeroImage] = useState(false);

  const [valuePropCards, setValuePropCards] = useState<HomepageValuePropCard[]>(defaultValuePropCards);
  const [initialValuePropCardsJson, setInitialValuePropCardsJson] = useState<string>(JSON.stringify(defaultValuePropCards));

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Success messages will be handled by toast

  const [availableGeneralImages, setAvailableGeneralImages] = useState<AvailableImage[]>([]);
  const [isLoadingAvailableImages, setIsLoadingAvailableImages] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  const getFullImageUrl = (relativePath: string | null | undefined): string | undefined => {
      return constructFullImageUrl(relativePath, backendBaseUrl);
  };

  useEffect(() => {
    const fetchHomepageSettings = async () => {
      setIsLoading(true);
      setIsLoadingAvailableImages(true);
      setError(null);
      try {
        const fetchedSettingsArray = await getAllSettings();
        const newInitialMap: Record<string, SiteSetting> = {};
        fetchedSettingsArray.forEach(setting => {
          newInitialMap[setting.key] = setting;
        });
        setInitialSettingsMap(newInitialMap);
        setCurrentSettingsMap(JSON.parse(JSON.stringify(newInitialMap))); 

        const heroImagesSetting = newInitialMap[KNOWN_SETTING_KEYS.HOME_HERO_IMAGES_JSON];
        if (heroImagesSetting?.value_json) {
            let parsedHeroImages: HomepageHeroImage[] = [];
            if (typeof heroImagesSetting.value_json === 'string') {
                try { parsedHeroImages = JSON.parse(heroImagesSetting.value_json); }
                catch (e) { console.error("Failed to parse hero images JSON string:", e); }
            } else if (Array.isArray(heroImagesSetting.value_json)) {
                parsedHeroImages = heroImagesSetting.value_json;
            }
            const validHeroImages = parsedHeroImages.filter(img => typeof img.url === 'string');
            setHeroImages(validHeroImages);
            setInitialHeroImagesJson(JSON.stringify(validHeroImages));
        } else {
            setHeroImages([]); 
            setInitialHeroImagesJson("[]");
        }

        const valuePropsSetting = newInitialMap[KNOWN_SETTING_KEYS.HOME_VALUE_PROP_CARDS_JSON];
        if (valuePropsSetting?.value_json) {
            let parsedValueProps: HomepageValuePropCard[] = defaultValuePropCards;
            if (typeof valuePropsSetting.value_json === 'string') {
                try { parsedValueProps = JSON.parse(valuePropsSetting.value_json); }
                catch (e) { console.error("Failed to parse value props JSON string:", e); }
            } else if (Array.isArray(valuePropsSetting.value_json) && valuePropsSetting.value_json.length > 0) {
                parsedValueProps = valuePropsSetting.value_json;
            }
            setValuePropCards(parsedValueProps);
            setInitialValuePropCardsJson(JSON.stringify(parsedValueProps));
        } else {
            setValuePropCards(JSON.parse(JSON.stringify(defaultValuePropCards)));
            setInitialValuePropCardsJson(JSON.stringify(defaultValuePropCards));
        }

        const availableImagesResponse = await getAvailableGeneralImages();
        setAvailableGeneralImages(availableImagesResponse.files || []);

      } catch (err: any) {
        console.error("Failed to fetch page data:", err);
        const errorMessage = err.message || err.response?.data?.detail || "Could not load page content settings.";
        setError(errorMessage);
        // toast.error(errorMessage); // Use toast for user-facing errors
        setHeroImages([]); 
        setInitialHeroImagesJson("[]");
        setValuePropCards(JSON.parse(JSON.stringify(defaultValuePropCards)));
        setInitialValuePropCardsJson(JSON.stringify(defaultValuePropCards));
        setAvailableGeneralImages([]);
      } finally {
        setIsLoading(false);
        setIsLoadingAvailableImages(false);
      }
    };
    fetchHomepageSettings();
  }, []);

  const handleSimpleSettingChange = (key: string, value: string, valueField: 'value_str' | 'value_text') => {
    setCurrentSettingsMap(prev => {
      const existingSetting = prev[key] || initialSettingsMap[key];

      // --- FIX: Explicitly map the valueField to the correct SettingValueType ---
      const valueType: SettingValueType = valueField === 'value_str' ? 'string' : 'text';
      const updatedSetting: SiteSetting = {
        id: existingSetting?.id || 0, 
        key,
        description: existingSetting?.description,
        value_str: null, value_text: null, value_bool: null, value_int: null, value_float: null, value_json: null,
        [valueField]: value, 
        value_type: valueType, 
        created_at: existingSetting?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      return { ...prev, [key]: updatedSetting };
    });
  };

  const handleHeroImageAltTextChange = (index: number, altText: string) => {
    const updatedImages = [...heroImages];
    updatedImages[index] = { ...updatedImages[index], alt_text: altText };
    setHeroImages(updatedImages);
  };

  const handleNewHeroImageFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setNewHeroImageFile(event.target.files[0]);
    } else {
      setNewHeroImageFile(null);
    }
  };

  const handleUploadAndAddHeroImage = async () => {
    if (!newHeroImageFile) {
      toast.error("Please select an image file to upload.");
      return;
    }
    if (!newHeroImageAltText.trim()) { // Added check for alt text on new uploads
        toast.error("Please provide alt text for the new image.");
        return;
    }
    setIsUploadingHeroImage(true);
    setError(null);
    try {
      const response: ImageUploadResponse = await uploadGeneralImage(newHeroImageFile); 
      
      if (response && response.image_url) {
        setHeroImages([...heroImages, { url: response.image_url, alt_text: newHeroImageAltText || '' }]);
        setNewHeroImageFile(null); 
        setNewHeroImageAltText(""); 
        if (fileInputRef.current) fileInputRef.current.value = "";
        toast.success("Hero image uploaded and added to list.");
        
        const availableImagesResponse = await getAvailableGeneralImages();
        setAvailableGeneralImages(availableImagesResponse.files || []);
      } else {
        throw new Error("Image URL not found in upload response.");
      }
    } catch (err: any) {
      console.error("Hero image upload failed:", err);
      const errorMessage = err.message || "Failed to upload hero image.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsUploadingHeroImage(false);
    }
  };

  const removeHeroImage = (indexToRemove: number) => {
    setHeroImages(heroImages.filter((_, i) => i !== indexToRemove));
    toast.info("Hero image removed from current list.");
  };
  
  // moveHeroImage function is removed as DND is removed

  const handleAddAvailableImageToHero = (imageToAdd: AvailableImage) => {
    const altText = prompt(`Enter alt text for "${imageToAdd.filename}":`, imageToAdd.filename.split('.')[0].replace(/[-_]/g, ' '));
    if (altText === null) return; 
    if (!altText.trim()) {
        toast.error('Alt text cannot be empty when adding an image.');
        return;
    }
    if (heroImages.some(heroImg => heroImg.url === imageToAdd.url)) {
        toast.info(`Image "${imageToAdd.filename}" is already in the hero list.`);
        return;
    }
    setHeroImages(prev => [...prev, { url: imageToAdd.url, alt_text: altText }]);
    toast.success(`"${imageToAdd.filename}" added to hero images list.`);
  };

  const handleValuePropCardChange = (index: number, field: keyof HomepageValuePropCard, value: string) => {
    const updatedCards = [...valuePropCards];
    if (field === 'id' || field === 'icon' || field === 'title' || field === 'description') {
        updatedCards[index] = { ...updatedCards[index], [field]: value };
        setValuePropCards(updatedCards);
    }
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setError(null); 

    const settingsToUpdate: SettingUpdatePayload[] = [];
    
     const addSettingToUpdate = (key: string, currentValue: any, initialValue: any, valueType: SettingValueType) => {
        const currentString = valueType === 'json' ? JSON.stringify(currentValue) : String(currentValue);
        const initialString = valueType === 'json' ? JSON.stringify(initialValue) : String(initialValue);
        
        if (currentString !== initialString) {
            const payload: Partial<SettingUpdatePayload> = { key, value_type: valueType };
            if (valueType === 'json') {
                payload.value_json = currentValue; 
            } else if (valueType === 'string') {
                payload.value_str = currentValue;
            } else if (valueType === 'text') {
                payload.value_text = currentValue;
            }
            settingsToUpdate.push(payload as SettingUpdatePayload);
        }
    };

    addSettingToUpdate(KNOWN_SETTING_KEYS.HOME_HERO_MAIN_TITLE, currentSettingsMap[KNOWN_SETTING_KEYS.HOME_HERO_MAIN_TITLE]?.value_str ?? '', JSON.stringify(initialSettingsMap[KNOWN_SETTING_KEYS.HOME_HERO_MAIN_TITLE]?.value_str ?? ''), 'string');
    addSettingToUpdate(KNOWN_SETTING_KEYS.HOME_HERO_SUBTITLE, currentSettingsMap[KNOWN_SETTING_KEYS.HOME_HERO_SUBTITLE]?.value_text ?? '', JSON.stringify(initialSettingsMap[KNOWN_SETTING_KEYS.HOME_HERO_SUBTITLE]?.value_text ?? ''), 'text');
    addSettingToUpdate(KNOWN_SETTING_KEYS.HOME_CTA_TITLE, currentSettingsMap[KNOWN_SETTING_KEYS.HOME_CTA_TITLE]?.value_str ?? '', JSON.stringify(initialSettingsMap[KNOWN_SETTING_KEYS.HOME_CTA_TITLE]?.value_str ?? ''), 'string');
    addSettingToUpdate(KNOWN_SETTING_KEYS.HOME_CTA_SUBTITLE, currentSettingsMap[KNOWN_SETTING_KEYS.HOME_CTA_SUBTITLE]?.value_text ?? '', JSON.stringify(initialSettingsMap[KNOWN_SETTING_KEYS.HOME_CTA_SUBTITLE]?.value_text ?? ''), 'text');

    const processedHeroImages = heroImages.map(img => ({ url: img.url, alt_text: img.alt_text || '' }));
    addSettingToUpdate(KNOWN_SETTING_KEYS.HOME_HERO_IMAGES_JSON, processedHeroImages, initialHeroImagesJson, 'json');
    
    addSettingToUpdate(KNOWN_SETTING_KEYS.HOME_VALUE_PROP_CARDS_JSON, valuePropCards, initialValuePropCardsJson, 'json');

    if (settingsToUpdate.length > 0) {
      try {
        const updatedSettingsArray = await updateSettings(settingsToUpdate); 
        const newInitialMap = { ...initialSettingsMap };
        const newCurrentMap = { ...currentSettingsMap };

        updatedSettingsArray.forEach(s => {
          newInitialMap[s.key] = s; 
          newCurrentMap[s.key] = JSON.parse(JSON.stringify(s)); 
          
          if (s.key === KNOWN_SETTING_KEYS.HOME_HERO_IMAGES_JSON && s.value_json) {
            const validHeroImages = (Array.isArray(s.value_json) ? s.value_json : []).filter((img: any) => typeof img.url === 'string');
            setInitialHeroImagesJson(JSON.stringify(validHeroImages)); 
            // setHeroImages(validHeroImages); // Optionally reset current list to saved state
          }
          if (s.key === KNOWN_SETTING_KEYS.HOME_VALUE_PROP_CARDS_JSON && s.value_json) {
            const validValueProps = Array.isArray(s.value_json) ? s.value_json : defaultValuePropCards;
            setInitialValuePropCardsJson(JSON.stringify(validValueProps));
            // setValuePropCards(validValueProps); // Optionally reset
          }
        });
        setInitialSettingsMap(newInitialMap);
        setCurrentSettingsMap(newCurrentMap); 
        toast.success("Homepage content updated successfully!");
      } catch (err: any) {
        console.error("Failed to save homepage content settings:", err);
        const errorMessage = err.message || err.response?.data?.detail || "Failed to save homepage content.";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } else {
      toast.info("No changes to save for homepage content.");
    }
    setIsSaving(false);
  };

  if (isLoading) {
    return <div className="container mx-auto px-4 py-20 flex justify-center items-center h-screen"><Loader2 className="h-12 w-12 animate-spin text-primary" /> <span className="ml-4 text-lg">Loading Content Editor...</span></div>;
  }

  return (
    // DndProvider removed
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-10 text-foreground bg-background">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-6">
        <div className="flex items-center">
          <Settings className="h-10 w-10 text-primary mr-4 flex-shrink-0" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Edit <span className="text-gradient-maroon-to-gold">Homepage Content</span>
            </h1>
            <p className="mt-1 text-muted-foreground"> Customize the main sections of your homepage. </p>
          </div>
        </div>
        <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors self-start sm:self-center w-full sm:w-auto">
              <Link to="/admin/dashboard">
                <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
              </Link>
            </Button>
      </header>

      {error && ( <div className="p-3 my-4 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center"> <AlertCircle className="h-5 w-5 mr-2" /> {error} </div> )}
      
      <div className="space-y-8">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Hero Section</CardTitle>
            <CardDescription>Customize the main banner title, subtitle, and images.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor={KNOWN_SETTING_KEYS.HOME_HERO_MAIN_TITLE}>Main Title</Label>
              <Input id={KNOWN_SETTING_KEYS.HOME_HERO_MAIN_TITLE} value={currentSettingsMap[KNOWN_SETTING_KEYS.HOME_HERO_MAIN_TITLE]?.value_str ?? ''} onChange={(e) => handleSimpleSettingChange(KNOWN_SETTING_KEYS.HOME_HERO_MAIN_TITLE, e.target.value, 'value_str')} />
            </div>
            <div>
              <Label htmlFor={KNOWN_SETTING_KEYS.HOME_HERO_SUBTITLE}>Subtitle/Tagline</Label>
              <Textarea id={KNOWN_SETTING_KEYS.HOME_HERO_SUBTITLE} value={currentSettingsMap[KNOWN_SETTING_KEYS.HOME_HERO_SUBTITLE]?.value_text ?? ''} onChange={(e) => handleSimpleSettingChange(KNOWN_SETTING_KEYS.HOME_HERO_SUBTITLE, e.target.value, 'value_text')} rows={2} />
            </div>
            
            <div className="space-y-4">
              <Label className="text-md font-semibold">Hero Images</Label>
              <CardDescription className="mb-2">Upload new images or select from existing ones for the hero banner.</CardDescription>
              
              <div className="p-4 border rounded-md space-y-3 bg-gray-50">
                <Label htmlFor="new-hero-image-file" className="font-medium">Upload New Hero Image</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                    <Input
                        id="new-hero-image-file"
                        ref={fileInputRef}
                        type="file"
                        accept="image/png, image/jpeg, image/webp, image/gif"
                        onChange={handleNewHeroImageFileChange}
                        className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                        disabled={isUploadingHeroImage}
                    />
                     <Input
                        type="text"
                        placeholder="Alt text (required)"
                        value={newHeroImageAltText}
                        onChange={(e) => setNewHeroImageAltText(e.target.value)}
                        disabled={isUploadingHeroImage}
                    />
                </div>
                <Button 
                    onClick={handleUploadAndAddHeroImage} 
                    disabled={!newHeroImageFile || !newHeroImageAltText.trim() || isUploadingHeroImage} 
                    size="sm" 
                    className="w-full sm:w-auto"
                >
                    {isUploadingHeroImage ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
                    Upload & Add to List
                </Button>
              </div>

              <div className="mt-4">
                <Label className="font-medium">Current Hero Images (Order cannot be changed here)</Label>
                {heroImages.length === 0 && !isLoading && <p className="text-sm text-muted-foreground py-2">No hero images configured for the homepage yet.</p>}
                <div>
                    {heroImages.map((img, index) => (
                        <HeroImageItem // Using the simplified HeroImageItem
                            key={`${img.url}-${index}`}
                            image={img}
                            index={index}
                            getFullImageUrl={getFullImageUrl}
                            handleHeroImageAltTextChange={handleHeroImageAltTextChange}
                            removeHeroImage={() => removeHeroImage(index)}
                        />
                    ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                    Note: Removing an image here only removes it from this list. It does not delete the file from the server.
                </p>
              </div>

              <div className="mt-6 pt-4 border-t">
                <Label className="text-md font-semibold">Available Images in General Uploads</Label>
                <CardDescription className="mb-3">Select from previously uploaded images to add to the hero banner.</CardDescription>
                {isLoadingAvailableImages && (
                    <div className="flex items-center text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading available images...
                    </div>
                )}
                {!isLoadingAvailableImages && availableGeneralImages.length === 0 && (
                    <p className="text-sm text-muted-foreground">No other images found in general uploads.</p>
                )}
                {!isLoadingAvailableImages && availableGeneralImages.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                        {availableGeneralImages.map(availImg => (
                            <Card key={availImg.url} className="overflow-hidden group relative">
                                <img 
                                    src={getFullImageUrl(availImg.url)} 
                                    alt={availImg.filename}
                                    className="w-full h-24 object-cover border-b bg-gray-100 group-hover:opacity-75 transition-opacity"
                                    onError={(e) => { (e.target as HTMLImageElement).src = `https://placehold.co/150x100/eee/ccc?text=Error`; }}
                                />
                                <CardContent className="p-2 text-center">
                                    <p className="text-xs break-all mb-1.5 truncate" title={availImg.filename}>{availImg.filename}</p>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="w-full text-xs"
                                        onClick={() => handleAddAvailableImageToHero(availImg)}
                                        title="Add this image to the hero section list"
                                    >
                                        <PlusCircle className="h-3.5 w-3.5 mr-1" /> Add to Hero
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Value Proposition Cards</CardTitle>
            <CardDescription>Edit the three main value proposition cards displayed on the homepage.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {valuePropCards.map((card, index) => (
              <div key={card.id || index} className="p-4 border rounded-md space-y-3 bg-gray-50 ">
                <h4 className="font-semibold text-md text-primary">Card {index + 1} (ID: {card.id})</h4>
                <div>
                  <Label htmlFor={`vp_title_${index}`}>Title</Label>
                  <Input id={`vp_title_${index}`} value={card.title} onChange={(e) => handleValuePropCardChange(index, 'title', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor={`vp_desc_${index}`}>Description</Label>
                  <Textarea id={`vp_desc_${index}`} value={card.description} onChange={(e) => handleValuePropCardChange(index, 'description', e.target.value)} rows={3} />
                </div>
                <div>
                  <Label htmlFor={`vp_icon_${index}`}>Icon Name (Lucide)</Label>
                  <Input id={`vp_icon_${index}`} value={card.icon} onChange={(e) => handleValuePropCardChange(index, 'icon', e.target.value)} placeholder="e.g., BarChart3, BookOpen, Lightbulb" />
                   <p className="text-xs text-muted-foreground mt-1"> Enter a valid Lucide icon name. Refer to Lucide documentation. </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>"Ready to Elevate" CTA Section</CardTitle>
            <CardDescription>Customize the title and subtitle for the main Call to Action block.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor={KNOWN_SETTING_KEYS.HOME_CTA_TITLE}>CTA Title</Label>
              <Input id={KNOWN_SETTING_KEYS.HOME_CTA_TITLE} value={currentSettingsMap[KNOWN_SETTING_KEYS.HOME_CTA_TITLE]?.value_str ?? ''} onChange={(e) => handleSimpleSettingChange(KNOWN_SETTING_KEYS.HOME_CTA_TITLE, e.target.value, 'value_str')} />
            </div>
            <div>
              <Label htmlFor={KNOWN_SETTING_KEYS.HOME_CTA_SUBTITLE}>CTA Subtitle</Label>
              <Textarea id={KNOWN_SETTING_KEYS.HOME_CTA_SUBTITLE} value={currentSettingsMap[KNOWN_SETTING_KEYS.HOME_CTA_SUBTITLE]?.value_text ?? ''} onChange={(e) => handleSimpleSettingChange(KNOWN_SETTING_KEYS.HOME_CTA_SUBTITLE, e.target.value, 'value_text')} rows={3} />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-12 flex justify-end">
        <Button onClick={handleSaveChanges} disabled={isSaving} size="lg" className="px-6 py-3">
          {isSaving ? <Loader2 className="animate-spin mr-2 h-5 w-5"/> : <Save className="mr-2 h-5 w-5"/>}
          Save Homepage Content
        </Button>
      </div>
    </div>
    // DndProvider removed
  );
};

export default EditHomepageContentPage;
